import { useCallback, useEffect, useMemo, useState } from 'react';
import { InteractionStatus } from '@azure/msal-browser';
import { useMsal } from '@azure/msal-react';
import accessManager, { OrganizationInfo } from '@core/utils/productAccessManager';
import { persistSplashOrganization } from '@core/utils/startLoader';
import useUserUid from './useUserUid';

export type { OrganizationInfo } from '@core/utils/productAccessManager';

export type UseOrganizationId = {
  organization: OrganizationInfo;
  setOrganization: (newOrg: OrganizationInfo) => void;
  isAcquiring: boolean;
  isError: boolean;
  error: any;
};

const useOrganization = (): UseOrganizationId => {
  const userUid = useUserUid();
  const [organization, setOrg] = useState(() => accessManager.getOrganization(userUid));
  // set acquiring to true to indicate an immediate acquiring process of product token
  const [isAcquiring, setIsAcquiring] = useState(true);
  const [error, setError] = useState<any>();
  const { instance, inProgress } = useMsal();

  useEffect(() => {
    setOrg(accessManager.getOrganization(userUid));
  }, [userUid]);

  // Remember the active org so the next page load (including the reload after
  // switching orgs) shows its logo on the index.html splash.
  const organizationId = organization?.organizationId;
  useEffect(() => {
    if (organizationId) persistSplashOrganization(organizationId);
  }, [organizationId]);

  useEffect(() => {
    // MSAL is still processing a redirect/interaction (e.g. the login
    // callback hasn't resolved into an active account yet) — acquiring a
    // product token now would fail with no account/API token available,
    // which previously fell through to instance.logoutRedirect() below
    // while the ORIGINAL redirect was still in flight. msal-browser refuses
    // to start a second interaction while one is already in progress, which
    // permanently stuck the app on the loading screen right after login.
    // Wait for inProgress to settle — msal-react re-renders consumers when
    // it does, so this effect naturally retries with a real account.
    if (inProgress !== InteractionStatus.None) {
      return;
    }

    // No organization to exchange a token for: an anonymous visitor, or a user
    // who hasn't picked one yet (OrganizationSetter renders the picker). There
    // is nothing to acquire, so settle the flag instead of leaving it stuck at
    // its initial `true` forever — consumers read it to decide whether
    // organization-scoped requests may go out.
    if (!organization) {
      setIsAcquiring(false);
      return;
    }

    async function getToken() {
      setIsAcquiring(true);

      try {
        // Concurrent callers share one in-flight exchange; see
        // productAccessManager.acquireProductTokenSilent.
        const accessToken = await accessManager.acquireProductTokenSilent();
        if (!accessToken) {
          accessManager.clearUserSession(userUid);
          instance.logoutRedirect();
        }
      } catch (err: any) {
        console.error(err);
        setError(err);
      } finally {
        setIsAcquiring(false);
      }
    }

    getToken();
  }, [organization, userUid, instance, inProgress]);

  const setOrganization = useCallback(
    (newOrg: OrganizationInfo) => {
      accessManager.removeOrganization(userUid);
      accessManager.setOrganization(newOrg, userUid);
      setOrg(newOrg);
      setIsAcquiring(true);
    },
    [userUid],
  );

  // Memoized because AuthProvider puts this straight into a context value read
  // by ~55 useProfile call sites — returning a fresh object every render made
  // AuthProvider's own useMemo a no-op and re-rendered all of them.
  return useMemo(
    () => ({
      error,
      isError: !error,
      isAcquiring,
      organization: organization ?? {},
      setOrganization,
    }),
    [error, isAcquiring, organization, setOrganization],
  );
};

export default useOrganization;
